'use client';
import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { useNavigate } from '@hooks/useNavigation';
import { useSchoolListing } from '@hooks/useSchoolListing';
import Info from '@icons/Info.svg';
import { useHeaderVisibilityStore } from '@providers/header-visibility-store-provider';
import { lightTheme } from '@styles/theme';
import { ROLES } from 'types';

import { Button } from '@components/Button';
import {
  BottomContent,
  Illustration,
  IllustrationWrapper,
  PageBottom,
  PageContent,
  PageGrid,
} from '@components/Common';
import { LoadingErrorHandler } from '@components/LoadingErrorHandler';
import { ImageContainer } from '@components/Onboarding/TeacherOnboarding';
import { ShiftTextGroup } from '@components/TextGroups';

import { Container, InfoWrapper, TextHeBottomInfo, TopContent } from './styles';

/**
 * Set School Listing before creating a first shift
 */
export const SetListingStep: React.FC = () => {
  const { t } = useTranslation('common');
  const { replace } = useNavigate();
  const setShowBackButton = useHeaderVisibilityStore((state) => state.setShowBackButton);
  const setOnBack = useHeaderVisibilityStore((state) => state.setOnBack);
  const { isPending: isListingPending, isError } = useSchoolListing();

  // Handle back navigation to shifts page
  const handleBack = useCallback(() => {
    replace(`/${ROLES.SCHOOL}/shifts`);
  }, [replace]);

  // Set up header back button
  useEffect(() => {
    setShowBackButton(true);
    setOnBack(handleBack);

    // Clean up on unmount
    return () => {
      setShowBackButton(false);
      setOnBack(undefined);
    };
  }, [setShowBackButton, setOnBack, handleBack]);

  const handleBottomButtonClick = () => {
    // Go to listing page to create/publish one
    replace(`/${ROLES.SCHOOL}/listing`);
  };

  return (
    <PageGrid className="full-height">
      <PageContent>
        <LoadingErrorHandler isLoading={isListingPending} isError={isError}>
          <Container>
            <TopContent>
              <ShiftTextGroup
                header={t('schoolShifts.newShifts')}
                title={t('menu.schoolPage')}
                description={t('schoolShifts.listingDescription')}
              />
              <ImageContainer>
                <IllustrationWrapper>
                  <Illustration
                    priority
                    fill
                    src={'/assets/images/Illustration-new-school.svg'}
                    alt={'Illustration-check'}
                  />
                </IllustrationWrapper>
              </ImageContainer>
            </TopContent>
            <InfoWrapper>
              <Info color={lightTheme.colors.typographyBodyLight} />
              <TextHeBottomInfo>{t('schoolShifts.bottomInfo')}</TextHeBottomInfo>
            </InfoWrapper>
          </Container>
        </LoadingErrorHandler>
      </PageContent>
      <PageBottom>
        <BottomContent>
          <Button
            label={t('schoolListing.checkListing')}
            type="button"
            onClick={handleBottomButtonClick}
            variant="primary"
          />
        </BottomContent>
      </PageBottom>
    </PageGrid>
  );
};
